/**
 * CoupleFlow — root component for the Couple Portrait flow.
 * Route: /flow/couple
 *
 * State machine driven from useCoupleFlow; switches CP1–CP7 via local screen state.
 * partnerUsername can be pre-filled from ?partnerUsername= query param.
 */

import { useState, useEffect } from 'react';
import { useCoupleFlow } from '../../../hooks/useCoupleFlow';
import { CP1Entry } from './CP1Entry';
import { CP2Waiting } from './CP2Waiting';
import { CP3Generating } from './CP3Generating';
import { CP4Reveal } from './CP4Reveal';
import { CP5Actions } from './CP5Actions';
import { CP6PublicPage } from './CP6PublicPage';
import { CP7Privacy } from './CP7Privacy';

type ScreenId = 'cp1' | 'cp2' | 'cp3' | 'cp4' | 'cp5' | 'cp6';

export function CoupleFlow(): JSX.Element {
  const [screen, setScreen] = useState<ScreenId>('cp1');
  const [showPrivacy, setShowPrivacy] = useState(false);

  // Read query params once. ?upsell=1 (from the post-compat upsell) selects the
  // discounted couple_portrait_upsell SKU (99); otherwise the standalone one (149).
  const params = new URLSearchParams(window.location.search);
  const partnerUsername = params.get('partnerUsername') ?? undefined;
  const skuCode = params.get('upsell') === '1' ? 'couple_portrait_upsell' : 'couple_portrait';

  const flow = useCoupleFlow(skuCode);

  // Drive screen from flow state
  useEffect(() => {
    switch (flow.state) {
      case 'idle':
      case 'initiating':
      case 'cancelled':
      case 'failed':
        setScreen('cp1');
        break;
      case 'waiting_partner':
        setScreen('cp2');
        break;
      case 'both_confirmed':
      case 'generating':
        setScreen('cp3');
        break;
      case 'completed':
        setScreen('cp4');
        break;
    }
  }, [flow.state]);

  if (showPrivacy) {
    return <CP7Privacy flow={flow} onClose={() => setShowPrivacy(false)} />;
  }

  switch (screen) {
    case 'cp1':
      return <CP1Entry flow={flow} partnerUsername={partnerUsername} skuCode={skuCode} />;
    case 'cp2':
      return <CP2Waiting flow={flow} />;
    case 'cp3':
      return <CP3Generating flow={flow} />;
    case 'cp4':
      return <CP4Reveal flow={flow} onActions={() => setScreen('cp5')} />;
    case 'cp5':
      return (
        <CP5Actions
          flow={flow}
          onPrivacy={() => setShowPrivacy(true)}
          onShare={() => setScreen('cp6')}
        />
      );
    case 'cp6':
      return <CP6PublicPage flow={flow} onBack={() => setScreen('cp5')} />;
    default:
      return <CP1Entry flow={flow} skuCode={skuCode} />;
  }
}
